'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from '../config/authConfig';

// Debug logging helper
const debug = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Dashboard]', ...args);
  }
};

// Helper function to calculate envelope hash (includes recipient details for change detection)
const calculateEnvelopeHash = (envelopes) => {
  if (!envelopes || envelopes.length === 0) return '';
  return JSON.stringify(envelopes.map(e => ({
    id: e.envelopeId,
    status: e.status,
    recipients: e.recipients?.map(r => ({
      name: r.name,
      email: r.email,
      status: r.status
    }))
  })));
};

// Retry logic for API calls
const fetchWithRetry = async (url, options, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    const response = await fetch(url, options);
    if (response.ok) {
      return response;
    }
    if (i === maxRetries - 1) {
      return response;
    }
    await new Promise(resolve => setTimeout(resolve, delay));
  }
};

export function useDashboard() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // ============================================================================
  // STATE DECLARATIONS
  // ============================================================================

  // Envelope data
  const [allEnvelopes, setAllEnvelopes] = useState([]);
  const [filteredEnvelopes, setFilteredEnvelopes] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Search and filter state
  const [searchEmails, setSearchEmails] = useState(['']);
  const [searchType, setSearchType] = useState('email');
  const [statusFilters, setStatusFilters] = useState(['in-progress']);
  const [dateFilter, setDateFilter] = useState('last-6-months');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });

  // Active search params - tracks the actual search being displayed (for polling to repeat)
  // This is separate from form state so "My Envelopes" can work without populating search box
  const [activeSearchParams, setActiveSearchParams] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Loading and error states
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isPolling, setIsPolling] = useState(false);  // True only during background 30-second refresh
  const [hasSearched, setHasSearched] = useState(false);

  // UI expansion state
  const [expandedEnvelopes, setExpandedEnvelopes] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});

  // Pending envelopes view
  const [isPendingView, setIsPendingView] = useState(false);
  const [awaitingSignatureCount, setAwaitingSignatureCount] = useState(0);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // User info
  const [userPhoto, setUserPhoto] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);

  // Signing state
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [signedEnvelopeId, setSignedEnvelopeId] = useState(null);
  const [processingEnvelopeId, setProcessingEnvelopeId] = useState(null);
  const [signingStatus, setSigningStatus] = useState(null);
  const [signingError, setSigningError] = useState(null);
  const [processingRecipientsMap, setProcessingRecipientsMap] = useState({});

  // Reassignment state
  const [canReassign, setCanReassign] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignEnvelopeId, setReassignEnvelopeId] = useState(null);
  const [reassignRecipient, setReassignRecipient] = useState(null);
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignmentSuccess, setReassignmentSuccess] = useState(null);

  // Reminder state
  const [sendingReminderKey, setSendingReminderKey] = useState(null); // "envelopeId:recipientId" while sending
  const [reminderSuccess, setReminderSuccess] = useState(null);

  // Void state
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidEnvelope, setVoidEnvelope] = useState(null);
  const [isVoiding, setIsVoiding] = useState(false);
  const [voidSuccess, setVoidSuccess] = useState(null);

  // Document removal state
  const [removeDocModalOpen, setRemoveDocModalOpen] = useState(false);
  const [removeDocEnvelopeId, setRemoveDocEnvelopeId] = useState(null);
  const [removeDocTarget, setRemoveDocTarget] = useState(null); // { documentId, name }
  const [isRemovingDoc, setIsRemovingDoc] = useState(false);
  const [removeDocSuccess, setRemoveDocSuccess] = useState(null);
  const [envelopeDocuments, setEnvelopeDocuments] = useState({}); // { [envelopeId]: documents[] }
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Document viewing state (non-destructive read/print path)
  const [isLoadingDocView, setIsLoadingDocView] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null); // documentId currently being fetched

  // Reminder silencing state
  const [remindersUpdatingId, setRemindersUpdatingId] = useState(null); // envelopeId in flight
  const [remindersSuccess, setRemindersSuccess] = useState(null);

  // Polling state
  const [lastPolled, setLastPolled] = useState(Date.now());
  const [updatesAvailable, setUpdatesAvailable] = useState(false);
  const [lastEnvelopeHash, setLastEnvelopeHash] = useState('');
  const [pollingState, setPollingState] = useState({
    lastPollTime: Date.now(),
    cooldownUntil: 0
  });
  const [lastUpdateButtonClick, setLastUpdateButtonClick] = useState(0);

  // MOTD state
  const [motd, setMotd] = useState(null);
  const [motdLoading, setMotdLoading] = useState(true);
  const [isMotdAdmin, setIsMotdAdmin] = useState(false);
  const [motdModalOpen, setMotdModalOpen] = useState(false);
  const [isSavingMotd, setIsSavingMotd] = useState(false);

  // Refs
  const lastFetchRef = useRef(0);
  const fetchInProgressRef = useRef(false);
  const fetchEnvelopesRef = useRef(null);
  const fetchAwaitingCountRef = useRef(null);
  const userEmailRef = useRef(null);

  // ============================================================================
  // AUTHENTICATION & TOKEN FUNCTIONS
  // ============================================================================

  const getAccessToken = useCallback(async () => {
    try {
      const account = accounts[0];
      if (!account) {
        throw new Error('No account found');
      }
      const tokenRequest = {
        scopes: loginRequest.scopes,
        account: account,
      };
      const response = await instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      const needsInteraction =
        error.name === 'InteractionRequiredAuthError' ||
        (error.name === 'BrowserAuthError' &&
          ['monitor_window_timeout', 'popup_window_error', 'empty_window_error']
            .includes(error.errorCode));

      if (needsInteraction) {
        try {
          const response = await instance.acquireTokenPopup({
            scopes: loginRequest.scopes
          });
          return response.accessToken;
        } catch (popupError) {
          debug('Popup token acquisition failed, falling back to redirect:', popupError.message);
          await instance.acquireTokenRedirect({ scopes: loginRequest.scopes });
          return null;
        }
      }
      throw error;
    }
  }, [instance, accounts]);

  // ============================================================================
  // USER INFO FUNCTIONS
  // ============================================================================

  const getUserInfo = useCallback(async () => {
    try {
      const account = accounts[0];
      if (!account) return null;

      const graphTokenRequest = {
        scopes: ["User.Read"],
        account: account,
      };
      const graphResponse = await instance.acquireTokenSilent(graphTokenRequest);

      // Fetch user profile
      let email = null;
      const profileResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { 'Authorization': `Bearer ${graphResponse.accessToken}` }
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserDisplayName(profileData.displayName);
        email = profileData.mail || profileData.userPrincipalName || '';
        setUserEmail(email);
      }

      // Fetch user photo
      try {
        const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { 'Authorization': `Bearer ${graphResponse.accessToken}` }
        });
        if (photoResponse.ok) {
          const photoBlob = await photoResponse.blob();
          const photoUrl = URL.createObjectURL(photoBlob);
          setUserPhoto(photoUrl);
        }
      } catch {
        debug('No user photo available');
      }

      return email;
    } catch (error) {
      debug('Error fetching user info:', error);
      const account = accounts[0];
      if (account) {
        setUserDisplayName(account.name || account.username);
      }
      return null;
    } finally {
      setIsUserInfoLoading(false);
    }
  }, [instance, accounts]);

  // ============================================================================
  // ENVELOPE FETCHING FUNCTIONS
  // ============================================================================

  const fetchEnvelopes = useCallback(async (shouldResetPage = false, isPendingViewParam = false, pageNumber = null) => {
    debug('fetchEnvelopes called with:', { shouldResetPage, isPendingViewParam, pageNumber, itemsPerPage });
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;
    setIsFetching(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      // For pending view, always show 50 items per page
      params.append('pageSize', isPendingViewParam ? '50' : itemsPerPage.toString());
      params.append('page', shouldResetPage ? '1' : (pageNumber || currentPage).toString());
      debug('Request params:', Object.fromEntries(params.entries()));

      // Handle status filters
      if (isPendingViewParam) {
        params.append('searchEmails', userEmail.toLowerCase());
        params.append('status', ['sent', 'delivered'].join(','));
      } else {
        // For user-initiated searches (shouldResetPage=true), use form state directly
        // For polling/pagination (shouldResetPage=false), use activeSearchParams if set
        const useActiveParams = !shouldResetPage && activeSearchParams;
        const effectiveSearchEmails = useActiveParams ? activeSearchParams.searchEmails : searchEmails;
        const effectiveSearchType = useActiveParams ? activeSearchParams.searchType : searchType;
        const effectiveStatusFilters = useActiveParams ? activeSearchParams.statusFilters : statusFilters;

        // Handle empty search as "all documents" search
        const hasValidSearchTerms = effectiveSearchEmails.some(term => term.trim());
        if (hasValidSearchTerms) {
          effectiveSearchEmails.forEach(term => {
            const trimmedTerm = term.trim();
            if (trimmedTerm) {
              if (effectiveSearchType === 'email') {
                params.append('searchEmails', trimmedTerm.toLowerCase());
              } else if (effectiveSearchType === 'subject') {
                params.append('subject', trimmedTerm);
              } else if (effectiveSearchType === 'envelopeId') {
                params.append('envelopeId', trimmedTerm);
              } else if (effectiveSearchType === 'name') {
                params.append('name', trimmedTerm);
              }
            }
          });
        }

        // Handle status filters for regular search
        if (!effectiveStatusFilters.includes('all')) {
          const apiStatuses = effectiveStatusFilters.flatMap(status => {
            if (status === 'in-progress') return ['sent', 'delivered'];
            return [status];
          });
          params.append('status', apiStatuses.join(','));
        }
      }

      // Add date filter parameters - use activeSearchParams only for polling/pagination
      const useActiveParamsForDate = !shouldResetPage && activeSearchParams;
      const effectiveDateFilter = useActiveParamsForDate ? activeSearchParams.dateFilter : dateFilter;
      const effectiveCustomDateRange = useActiveParamsForDate ? activeSearchParams.customDateRange : customDateRange;
      params.append('dateFilter', effectiveDateFilter);
      if (effectiveDateFilter === 'custom') {
        if (effectiveCustomDateRange.from) params.append('fromDate', effectiveCustomDateRange.from);
        if (effectiveCustomDateRange.to) params.append('toDate', effectiveCustomDateRange.to);
      }

      const requestUrl = `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes?${params.toString()}`;
      debug('Search request URL:', requestUrl);

      const response = await fetchWithRetry(requestUrl, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'X-DocuSign-Environment': process.env.NODE_ENV === 'development' ? 'demo' : 'production'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch envelopes: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      debug('Response data:', {
        totalCount: data.totalCount,
        envelopesReceived: data.envelopes?.length,
        requestedPageSize: itemsPerPage
      });

      // Process all envelopes first
      let allProcessedData = data.envelopes || [];

      if (isPendingViewParam) {
        debug('Processing pending view...');

        // Get unique envelopes first
        const uniqueEnvelopes = Array.from(new Set(allProcessedData.map(e => e.envelopeId)))
          .map(id => allProcessedData.find(e => e.envelopeId === id));

        debug(`Found ${uniqueEnvelopes.length} unique envelopes before filtering`);

        // Filter for pending envelopes - only those where current user needs to sign
        allProcessedData = uniqueEnvelopes.filter(envelope => {
          const isActive = ['sent', 'delivered'].includes(envelope.status?.toLowerCase());
          const isNotDeleted = !['deleted', 'voided'].includes(envelope.status?.toLowerCase());
          const needsUserSignature = envelope.recipients?.some(recipient =>
            recipient.email?.toLowerCase() === userEmail?.toLowerCase() &&
            ['sent', 'delivered'].includes(recipient.status?.toLowerCase())
          );

          return isActive && isNotDeleted && needsUserSignature;
        });

        debug(`Found ${allProcessedData.length} envelopes needing signature after filtering`);
      }

      // Handle data and pagination differently for pending vs normal view
      let totalCount;
      let filteredData;

      if (isPendingViewParam) {
        totalCount = allProcessedData.length;
        const startIndex = (shouldResetPage ? 0 : (pageNumber ? pageNumber - 1 : currentPage - 1)) * 50;
        const endIndex = Math.min(startIndex + 50, totalCount);
        filteredData = allProcessedData.slice(startIndex, endIndex);
      } else {
        // For normal search, use API pagination
        totalCount = data.totalCount || data.envelopes.length;
        filteredData = data.envelopes;
      }

      debug(`Showing results: total count ${totalCount}`);

      // Compare data to prevent unnecessary re-renders (reduces visual blink on refresh)
      // Must include recipient details -- reassignment changes name/email without changing envelope status
      const currentHash = calculateEnvelopeHash(filteredEnvelopes);
      const newHash = calculateEnvelopeHash(filteredData);
      const dataChanged = currentHash !== newHash || totalItems !== totalCount;

      if (dataChanged) {
        setFilteredEnvelopes(filteredData);
        setAllEnvelopes(isPendingViewParam ? allProcessedData : filteredData);
        setTotalItems(totalCount);
      }

      if (shouldResetPage) {
        setCurrentPage(1);
        // Update activeSearchParams so polling uses the new filter values
        if (!isPendingViewParam) {
          setActiveSearchParams({
            searchEmails: [...searchEmails],
            searchType,
            statusFilters: [...statusFilters],
            dateFilter,
            customDateRange: { ...customDateRange }
          });
        }
      }
      setHasSearched(true);

      // Synchronize awaiting signature count with fetched data
      // This ensures the header count updates at the same time as the envelope list
      if (isPendingViewParam && userEmail) {
        // In pending view, allProcessedData already contains all pending envelopes
        // Use this count directly instead of waiting for a separate API call
        debug('Syncing awaiting count from pending view data:', allProcessedData.length);
        setAwaitingSignatureCount(allProcessedData.length);
        setIsAuthLoading(false);
      }

    } catch (err) {
      debug('Fetch error:', err);
      setError(err.message || 'Failed to fetch envelopes');
      setFilteredEnvelopes([]);
      setTotalItems(0);
      setHasSearched(true);
    } finally {
      fetchInProgressRef.current = false;
      setIsFetching(false);
    }
  }, [currentPage, itemsPerPage, statusFilters, dateFilter, customDateRange, searchEmails, searchType, getAccessToken, userEmail, filteredEnvelopes, totalItems, activeSearchParams]);

  // Keep ref updated with latest fetchEnvelopes for polling
  useEffect(() => {
    fetchEnvelopesRef.current = fetchEnvelopes;
  }, [fetchEnvelopes]);

  const fetchAwaitingSignatureCount = useCallback(async (email) => {
    if (!email) {
      debug('Missing email for fetchAwaitingSignatureCount');
      return 0;
    }

    debug('Starting fetchAwaitingSignatureCount for', email);
    try {
      const token = await getAccessToken();
      if (!token) {
        debug('No token available for fetching awaiting count');
        return 0;
      }

      const params = new URLSearchParams({ email });
      const url = `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/awaiting-count?${params}`;
      debug('Fetching awaiting count:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      debug('fetchAwaitingSignatureCount result:', data.count);

      setAwaitingSignatureCount(data.count);
      return data.count;
    } catch (error) {
      console.error('Error in fetchAwaitingSignatureCount:', error);
      setAwaitingSignatureCount(0);
      return 0;
    } finally {
      setIsAuthLoading(false);
    }
  }, [getAccessToken]);

  // Keep refs updated for polling
  useEffect(() => {
    fetchAwaitingCountRef.current = fetchAwaitingSignatureCount;
  }, [fetchAwaitingSignatureCount]);

  useEffect(() => {
    userEmailRef.current = userEmail;
  }, [userEmail]);

  // ============================================================================
  // SEARCH FUNCTIONS
  // ============================================================================

  const handleSearch = useCallback(async () => {
    if (fetchInProgressRef.current) return;

    // Clear pending view
    if (isPendingView) {
      setIsPendingView(false);
    }

    // Update activeSearchParams to match current form state
    // This ensures polling repeats the same search
    setActiveSearchParams({
      searchEmails: [...searchEmails],
      searchType,
      statusFilters: [...statusFilters],
      dateFilter,
      customDateRange: { ...customDateRange }
    });

    setCurrentPage(1);
    setExpandedEnvelopes({});
    setExpandedDetails({});
    await fetchEnvelopes(true);
  }, [isPendingView, fetchEnvelopes, searchEmails, searchType, statusFilters, dateFilter, customDateRange]);

  const handleSearchInput = useCallback((value) => {
    setSearchEmails([value]);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchEmails(['']);
    setStatusFilters(['in-progress']);
    setDateFilter('last-6-months');
    setCustomDateRange({ from: '', to: '' });
    setActiveSearchParams(null);  // Clear active search so polling uses form state
    setCurrentPage(1);
    setFilteredEnvelopes([]);
    setAllEnvelopes([]);
    setTotalItems(0);
    setExpandedEnvelopes({});
    setExpandedDetails({});
    setHasSearched(false);
    setIsPendingView(false);
    setUpdatesAvailable(false);
    setError(null);
    setSigningError(null);
  }, []);

  // ============================================================================
  // PENDING ENVELOPES VIEW
  // ============================================================================

  const showPendingEnvelopes = useCallback(async () => {
    if (!userEmail) return;

    // Only clear state when first entering pending view, not on refresh.
    // fetchEnvelopes replaces the data atomically, so clearing here just
    // causes a flash of "No documents found" if the fetch guard bails.
    if (!isPendingView) {
      setFilteredEnvelopes([]);
      setAllEnvelopes([]);
      setTotalItems(0);
    }
    setHasSearched(true);

    setIsPendingView(true);
    setStatusFilters(['in-progress']);
    setDateFilter('last-6-months');
    setCustomDateRange({ from: '', to: '' });
    setCurrentPage(1);

    debug('Starting pending envelopes fetch...');

    try {
      await fetchEnvelopes(true, true);
    } catch (error) {
      console.error('Error fetching pending envelopes:', error);
    }
  }, [userEmail, isPendingView, fetchEnvelopes]);

  const toggleEnvelopeView = useCallback(async () => {
    debug('Toggle clicked. Current isPendingView:', isPendingView);

    // Clear any existing updates banner when switching views
    setUpdatesAvailable(false);

    if (isPendingView) {
      debug('Was pending, clearing to normal view');
      setIsPendingView(false);
      setFilteredEnvelopes([]);
      setAllEnvelopes([]);
      setTotalItems(0);
      setStatusFilters(['in-progress']);
      setDateFilter('last-6-months');
      setCurrentPage(1);
      setSearchEmails(['']);
      setExpandedEnvelopes({});
      setExpandedDetails({});
      setHasSearched(false);
    } else {
      debug('Was normal, switching to pending view');
      await showPendingEnvelopes();
    }

    debug('Toggle complete. New isPendingView will be:', !isPendingView);
  }, [isPendingView, showPendingEnvelopes]);

  // ============================================================================
  // SIGNING FUNCTIONS
  // ============================================================================

  // Report signing errors to backend for alerting (fails silently)
  const reportSigningError = useCallback(async (envelopeId, errorMessage, routingOrder) => {
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/telemetry/error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          envelopeId,
          errorMessage,
          userEmail: userEmail || null,
          routingOrder,
          timestamp: new Date().toISOString()
        })
      });
    } catch (e) {
      // Silently fail - don't let telemetry errors affect user experience
      debug('Failed to report signing error to telemetry:', e);
    }
  }, [userEmail]);

  const getSigningUrl = useCallback(async (envelopeId, routingOrder = null) => {
    try {
      const token = await getAccessToken();
      const returnUrl = window.location.origin + window.location.pathname;

      let url = `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}/signing-url?returnUrl=${encodeURIComponent(returnUrl)}`;
      if (routingOrder !== null) {
        url += `&routingOrder=${routingOrder}`;
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.url;
    } catch (err) {
      debug('Error getting signing URL:', err);
      // Report error for alerting (fire and forget)
      reportSigningError(envelopeId, err.message, routingOrder);
      throw err;
    }
  }, [getAccessToken, reportSigningError]);

  const checkSigningCompletion = useCallback(async (envelopeId) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const currentUserRecipient = data.recipients?.find(r =>
          r.email?.toLowerCase() === userEmail?.toLowerCase()
        );

        if (currentUserRecipient?.status === 'completed') {
          return true;
        }
      }
      return false;
    } catch (err) {
      debug('Error checking signing completion:', err);
      return false;
    }
  }, [getAccessToken, userEmail]);

  const handleSigningRequest = useCallback(async (envelopeId, event, routingOrder = null) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setSigningError(null);
    setProcessingEnvelopeId(envelopeId);
    setSigningInProgress(true);
    setSigningStatus('Getting signing URL...');

    try {
      const signingUrl = await getSigningUrl(envelopeId, routingOrder);

      if (signingUrl) {
        // Store processing state
        const processingData = {
          envelopeId,
          startTime: Date.now(),
          routingOrder
        };
        localStorage.setItem('processingEnvelopes', JSON.stringify(processingData));

        setSigningStatus('Opening signing window...');
        window.open(signingUrl, 'DocuSignSigning', 'width=1000,height=800,scrollbars=yes,resizable=yes');
      }
    } catch (err) {
      debug('Signing error:', err);
      setSigningError(err.message || 'Failed to get signing URL');
      setSigningInProgress(false);
      setProcessingEnvelopeId(null);
      setSigningStatus(null);
    }
  }, [getSigningUrl]);

  // ============================================================================
  // REASSIGNMENT FUNCTIONS
  // ============================================================================

  const openReassignModal = useCallback((envelope, recipient) => {
    setReassignEnvelopeId(envelope.envelopeId);
    setReassignRecipient(recipient);
    setReassignModalOpen(true);
  }, []);

  const handleReassign = useCallback(async (newEmail, newName, reason) => {
    setIsReassigning(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${reassignEnvelopeId}/recipients/${encodeURIComponent(reassignRecipient.docuSignRecipientId)}/reassign`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            newEmail: newEmail,
            newName: newName,
            justification: reason
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Success
      setReassignModalOpen(false);
      setReassignRecipient(null);
      setReassignEnvelopeId(null);
      setReassignmentSuccess({
        message: `Successfully reassigned from ${reassignRecipient.name} to ${newName}`
      });

      // Auto-clear success message after 5 seconds
      setTimeout(() => setReassignmentSuccess(null), 5000);

      // Refresh envelope list
      if (isPendingView) {
        await showPendingEnvelopes();
      } else if (hasSearched) {
        await fetchEnvelopes(false);
      }

      // Update count
      if (userEmail) {
        await fetchAwaitingSignatureCount(userEmail);
      }

    } catch (err) {
      debug('Reassign error:', err);
      setError(err.message || 'Failed to reassign recipient');
    } finally {
      setIsReassigning(false);
    }
  }, [getAccessToken, reassignEnvelopeId, reassignRecipient, isPendingView, hasSearched, showPendingEnvelopes, fetchEnvelopes, fetchAwaitingSignatureCount, userEmail]);

  // ============================================================================
  // REMINDER FUNCTIONS
  // ============================================================================

  const handleSendReminder = useCallback(async (envelopeId, recipientId, recipientName) => {
    const key = `${envelopeId}:${recipientId}`;
    setSendingReminderKey(key);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}/recipients/${encodeURIComponent(recipientId)}/remind`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      setReminderSuccess({
        message: `Reminder sent to ${recipientName}`
      });

      // Auto-clear success message after 5 seconds
      setTimeout(() => setReminderSuccess(null), 5000);

    } catch (err) {
      debug('Reminder error:', err);
      setError(err.message || 'Failed to send reminder');
    } finally {
      setSendingReminderKey(null);
    }
  }, [getAccessToken]);

  // ============================================================================
  // VOID FUNCTIONS
  // ============================================================================

  const openVoidModal = useCallback((envelope) => {
    setVoidEnvelope(envelope);
    setVoidModalOpen(true);
  }, []);

  const handleVoidEnvelope = useCallback(async ({ reason }) => {
    if (!voidEnvelope) return;
    setIsVoiding(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${voidEnvelope.envelopeId}/void`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reason })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Success
      setVoidModalOpen(false);
      setVoidEnvelope(null);
      setVoidSuccess({
        message: `Envelope "${voidEnvelope.subject || 'Untitled'}" has been voided`
      });

      // Auto-clear success message after 5 seconds
      setTimeout(() => setVoidSuccess(null), 5000);

      // Refresh envelope list
      if (isPendingView) {
        await showPendingEnvelopes();
      } else if (hasSearched) {
        await fetchEnvelopes(false);
      }

      // Update count
      if (userEmail) {
        await fetchAwaitingSignatureCount(userEmail);
      }

    } catch (err) {
      debug('Void error:', err);
      setError(err.message || 'Failed to void envelope');
    } finally {
      setIsVoiding(false);
    }
  }, [getAccessToken, voidEnvelope, isPendingView, hasSearched, showPendingEnvelopes, fetchEnvelopes, fetchAwaitingSignatureCount, userEmail]);

  // ============================================================================
  // DOCUMENT FUNCTIONS
  // ============================================================================

  const fetchEnvelopeDocuments = useCallback(async (envelopeId) => {
    if (!envelopeId) return null;
    setIsLoadingDocuments(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}/documents`,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setEnvelopeDocuments(prev => ({ ...prev, [envelopeId]: data.documents }));
      return data.documents;
    } catch (err) {
      debug('Fetch documents error:', err);
      return null;
    } finally {
      setIsLoadingDocuments(false);
    }
  }, [getAccessToken]);

  /**
   * Opens a document's PDF in a new tab for reading or printing, WITHOUT modifying
   * the envelope.
   *
   * This is the safe alternative to reassignment. Two of the four 2026-07 consent
   * incidents happened because staff had no way to see a document from DocuSpa, so
   * they reassigned the patient's signing slot to themselves to get at it - which
   * removed the patient's access as a side effect.
   *
   * A plain window.open(url) cannot be used: the endpoint requires a bearer token,
   * so the PDF is fetched with auth and turned into an object URL. Same pattern as
   * the Graph profile photo fetch above.
   *
   * Pass documentId 'combined' to open the whole packet in one DocuSign call.
   */
  const handleViewDocument = useCallback(async (envelopeId, documentId) => {
    if (!envelopeId || !documentId) return;

    // The tab must be opened synchronously inside the click handler or popup
    // blockers will kill it - we cannot open it after awaiting the fetch.
    const viewerTab = window.open('', '_blank');

    setIsLoadingDocView(true);
    setViewingDocId(documentId);
    setError(null);

    let objectUrl = null;
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}/documents/${documentId}/content`,
        {
          headers: {
            'Accept': 'application/pdf',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const pdfBlob = await response.blob();
      objectUrl = URL.createObjectURL(pdfBlob);

      if (viewerTab) {
        viewerTab.location.href = objectUrl;
      } else {
        // Popup blocked - fall back to same-tab navigation rather than failing.
        window.location.href = objectUrl;
      }

      // Revoke well after the tab has loaded the blob. Revoking immediately races
      // the navigation and yields a blank viewer.
      setTimeout(() => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (err) {
      debug('View document error:', err);
      if (viewerTab) viewerTab.close();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setError(err.message || 'Failed to open document');
    } finally {
      setIsLoadingDocView(false);
      setViewingDocId(null);
    }
  }, [getAccessToken]);

  /**
   * Turns an envelope's automatic reminder emails on or off.
   *
   * WHY: envelopes remind after 1 day then every 3 days against a 120-day expiry. A
   * duplicate Transport Consent had emailed a patient's partner ~38 times for a form
   * he did not need to sign. Until now the only ways to stop it were VOID - which is
   * destructive and impossible on an envelope holding valid signatures - or waiting
   * out the expiry. Samantha asked whether the envelope could be voided; what she
   * wanted was for it to stop emailing her.
   *
   * Reversible, and the state shows as a badge in the envelope list. That visibility
   * matters more than it looks: silencing an outstanding consent means a patient
   * quietly stops being chased. Visible, that self-corrects. Invisible, it is a trap.
   *
   * Updates the local list in place so the badge flips immediately without refetching.
   */
  const handleSetReminders = useCallback(async (envelopeId, enabled, reason = null) => {
    if (!envelopeId) return false;

    setRemindersUpdatingId(envelopeId);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${envelopeId}/reminders`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ enabled, reason })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Reflect the new state locally so the badge updates without a refetch.
      setFilteredEnvelopes(prev => prev.map(e =>
        e.envelopeId === envelopeId
          ? { ...e, remindersEnabled: data.remindersEnabled }
          : e
      ));

      setRemindersSuccess({
        message: enabled
          ? 'Automatic reminders resumed for this envelope'
          : 'Automatic reminders silenced. The envelope is still open and can still be signed.'
      });
      setTimeout(() => setRemindersSuccess(null), 6000);

      // The backend reports whether the durable audit row was written. If the
      // EnvelopeActionLogs migration has not been applied, surface it rather than
      // letting a state change go silently unrecorded.
      if (data.auditPersisted === false) {
        debug('Reminder change succeeded but the audit row was NOT persisted');
      }

      return true;
    } catch (err) {
      debug('Set reminders error:', err);
      setError(err.message || 'Failed to change reminder setting');
      return false;
    } finally {
      setRemindersUpdatingId(null);
    }
  }, [getAccessToken]);

  const openRemoveDocModal = useCallback((envelopeId, document) => {
    setRemoveDocEnvelopeId(envelopeId);
    setRemoveDocTarget(document);
    setRemoveDocModalOpen(true);
  }, []);

  const handleRemoveDocument = useCallback(async ({ reason }) => {
    if (!removeDocEnvelopeId || !removeDocTarget) return;
    setIsRemovingDoc(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/envelopes/${removeDocEnvelopeId}/documents/${removeDocTarget.documentId}/remove`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reason })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Capture before clearing state
      const envelopeId = removeDocEnvelopeId;
      const docName = removeDocTarget.name;

      // Success - close modal and clear targets
      setRemoveDocModalOpen(false);
      setRemoveDocTarget(null);
      setRemoveDocEnvelopeId(null);

      let successMessage = `Document "${docName}" has been removed`;
      if (result.orphanedRecipients && result.orphanedRecipients.length > 0) {
        const names = result.orphanedRecipients.map(r => r.name).join(', ');
        successMessage += `. Warning: ${names} now has no remaining signature fields.`;
      }

      setRemoveDocSuccess({ message: successMessage });
      setTimeout(() => setRemoveDocSuccess(null), 8000); // Longer timeout for orphan warnings

      // Re-fetch documents for this envelope (updates cache with remaining docs)
      await fetchEnvelopeDocuments(envelopeId);

      // Refresh envelope list
      if (isPendingView) {
        await showPendingEnvelopes();
      } else if (hasSearched) {
        await fetchEnvelopes(false);
      }

      if (userEmail) {
        await fetchAwaitingSignatureCount(userEmail);
      }

    } catch (err) {
      debug('Remove document error:', err);
      setError(err.message || 'Failed to remove document');
    } finally {
      setIsRemovingDoc(false);
    }
  }, [getAccessToken, removeDocEnvelopeId, removeDocTarget, isPendingView, hasSearched, showPendingEnvelopes, fetchEnvelopes, fetchEnvelopeDocuments, fetchAwaitingSignatureCount, userEmail]);

  const searchTenantUsers = useCallback(async (query) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/api/user/search?query=${encodeURIComponent(query)}`,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (err) {
      debug('User search error:', err);
      return [];
    }
  }, [getAccessToken]);

  // ============================================================================
  // MOTD FUNCTIONS
  // ============================================================================

  const fetchMOTD = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/system-messages/MOTD`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMotd(data);
      } else if (response.status === 404) {
        setMotd(null);
      }
    } catch (err) {
      debug('Error fetching MOTD:', err);
    } finally {
      setMotdLoading(false);
    }
  }, [getAccessToken]);

  const fetchMotdAdminStatus = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/system-messages/admin-status`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsMotdAdmin(data.isAdmin);
      }
    } catch (err) {
      debug('Error fetching MOTD admin status:', err);
    }
  }, [getAccessToken]);

  const handleSaveMotd = useCallback(async (newValue, isActive) => {
    setIsSavingMotd(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/system-messages/MOTD`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: newValue,
          isActive: isActive
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save MOTD');
      }

      const data = await response.json();
      setMotd(data);
      setMotdModalOpen(false);
    } catch (err) {
      debug('Error saving MOTD:', err);
      throw err;
    } finally {
      setIsSavingMotd(false);
    }
  }, [getAccessToken]);

  // ============================================================================
  // PERMISSIONS FUNCTIONS
  // ============================================================================

  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/user/permissions`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCanReassign(data.canReassign || false);
      }
    } catch (err) {
      debug('Error fetching permissions:', err);
    }
  }, [getAccessToken]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const debouncedFetch = useCallback(async (fetchFn) => {
    const now = Date.now();
    if (now - lastFetchRef.current < 500) {
      debug('Debouncing fetch');
      return;
    }
    await fetchFn();
  }, []);

  const toggleEnvelopeExpansion = useCallback((envelopeId) => {
    setExpandedEnvelopes(prev => ({
      ...prev,
      [envelopeId]: !prev[envelopeId]
    }));
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    // API returns UTC timestamps without 'Z' suffix - append it so JS parses as UTC
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const hasSequentialSigning = useCallback((envelope) => {
    const routingOrders = envelope.recipients
      ?.map(r => r.routingOrder)
      .filter(o => o !== undefined && o !== null);
    if (!routingOrders || routingOrders.length === 0) return false;
    const uniqueOrders = [...new Set(routingOrders)];
    return uniqueOrders.length > 1;
  }, []);

  const getSignerStatus = useCallback((recipient) => {
    switch (recipient.status?.toLowerCase()) {
      case 'completed':
        return 'bg-[#008455]';
      case 'delivered':
      case 'sent':
        return 'bg-yellow-400';
      case 'declined':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  }, []);

  const getSignerIcon = useCallback((status) => {
    // Returns icon component - this will be handled in the view
    return status;
  }, []);

  const handleLogout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  }, [instance]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initialize user info and permissions
  useEffect(() => {
    const initializeDashboard = async () => {
      if (isAuthenticated && accounts.length > 0) {
        const email = await getUserInfo();
        fetchUserPermissions();
        if (email) {
          await fetchAwaitingSignatureCount(email);
        }
        fetchMOTD();
        fetchMotdAdminStatus();
        setLoading(false);
      }
    };
    initializeDashboard();
  }, [isAuthenticated, accounts, getUserInfo, fetchUserPermissions, fetchAwaitingSignatureCount, fetchMOTD, fetchMotdAdminStatus]);

  // Status filter auto-fetch
  useEffect(() => {
    if (hasSearched && !isFetching) {
      debouncedFetch(() => fetchEnvelopes(true));
    }
  }, [statusFilters]);

  // Date filter auto-fetch (for preset options, not custom)
  useEffect(() => {
    if (hasSearched && !isFetching && dateFilter !== 'custom') {
      debouncedFetch(() => fetchEnvelopes(true));
    }
  }, [dateFilter]);

  // Independent polling for awaiting signature count (lightweight endpoint, always runs)
  useEffect(() => {
    if (!userEmail) return;

    let countInterval = null;

    const startCountPolling = () => {
      if (countInterval) return;
      debug('Starting awaiting count polling (30s)');
      countInterval = setInterval(() => {
        if (fetchAwaitingCountRef.current && userEmailRef.current) {
          debug('Count poll: updating awaiting signature count...');
          fetchAwaitingCountRef.current(userEmailRef.current);
        }
      }, 30000);
    };

    const stopCountPolling = () => {
      if (countInterval) {
        debug('Pausing awaiting count polling (tab hidden)');
        clearInterval(countInterval);
        countInterval = null;
      }
    };

    const handleCountVisibility = () => {
      if (document.hidden) {
        stopCountPolling();
      } else {
        if (fetchAwaitingCountRef.current && userEmailRef.current) {
          fetchAwaitingCountRef.current(userEmailRef.current);
        }
        startCountPolling();
      }
    };

    document.addEventListener('visibilitychange', handleCountVisibility);
    if (!document.hidden) startCountPolling();

    return () => {
      stopCountPolling();
      document.removeEventListener('visibilitychange', handleCountVisibility);
    };
  }, [userEmail]);

  // Polling for envelope list updates
  // Uses fetchEnvelopesRef to avoid dependency on fetchEnvelopes (which changes frequently)
  useEffect(() => {
    if (!hasSearched || !filteredEnvelopes.length) return;

    let pollInterval = null;

    const startPolling = () => {
      if (pollInterval) return;
      debug('Starting envelope list polling (30s)');
      pollInterval = setInterval(async () => {
        const currentNow = Date.now();
        if (currentNow >= pollingState.cooldownUntil && fetchEnvelopesRef.current) {
          debug('Polling: fetching envelopes...');
          setIsPolling(true);
          try {
            await fetchEnvelopesRef.current(false, isPendingView);
          } finally {
            setIsPolling(false);
          }
          setLastPolled(currentNow);
        }
      }, 30000);
    };

    const stopPolling = () => {
      if (pollInterval) {
        debug('Pausing envelope list polling (tab hidden)');
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        debug('Tab visible - immediate refresh + resume polling');
        if (fetchEnvelopesRef.current) {
          fetchEnvelopesRef.current(false, isPendingView);
        }
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    if (!document.hidden) startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasSearched, filteredEnvelopes.length, pollingState.cooldownUntil, isPendingView]);

  // Window focus handler for signing completion
  useEffect(() => {
    const handleFocus = async () => {
      const processingData = localStorage.getItem('processingEnvelopes');
      if (processingData) {
        try {
          const { envelopeId, startTime } = JSON.parse(processingData);
          const elapsed = Date.now() - startTime;

          // Only check if signing window was open for at least 5 seconds
          if (elapsed > 5000 && processingEnvelopeId === envelopeId) {
            debug('Checking signing completion for', envelopeId);
            const completed = await checkSigningCompletion(envelopeId);

            if (completed) {
              setSignedEnvelopeId(envelopeId);
              setSigningInProgress(false);
              setProcessingEnvelopeId(null);
              setSigningStatus(null);
              localStorage.removeItem('processingEnvelopes');

              // Refresh data - order optimized for synchronized updates
              if (isPendingView) {
                // In pending view, showPendingEnvelopes will update both
                // the envelope list AND the awaiting count atomically
                await showPendingEnvelopes();
              } else if (hasSearched) {
                // In normal view, update envelopes first for immediate feedback
                await fetchEnvelopes(false);
                // Then refresh the accurate count in background
                if (userEmail) {
                  fetchAwaitingSignatureCount(userEmail); // Don't await - let it run async
                }
              } else if (userEmail) {
                // No search active, just refresh the count
                await fetchAwaitingSignatureCount(userEmail);
              }

              // Clear signed indicator after 3 seconds
              setTimeout(() => setSignedEnvelopeId(null), 3000);
            } else {
              // User closed popup without completing - clear signing states
              // This prevents UI from being stuck in "Signing..." state
              debug('Signing not completed, clearing signing states for', envelopeId);
              setSigningInProgress(false);
              setProcessingEnvelopeId(null);
              setSigningStatus(null);
              localStorage.removeItem('processingEnvelopes');
            }
          }
        } catch (err) {
          debug('Error checking signing completion:', err);
          // On error, also clear states to prevent getting stuck
          setSigningInProgress(false);
          setProcessingEnvelopeId(null);
          setSigningStatus(null);
          localStorage.removeItem('processingEnvelopes');
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [processingEnvelopeId, checkSigningCompletion, fetchAwaitingSignatureCount, isPendingView, hasSearched, showPendingEnvelopes, fetchEnvelopes, userEmail]);

  // Signing timeout - forcibly clear states after 30 seconds regardless of completion
  useEffect(() => {
    if (!signingInProgress) return;

    const timeout = setTimeout(() => {
      debug('Signing timeout reached (30s), clearing signing states');
      setSigningInProgress(false);
      setProcessingEnvelopeId(null);
      setSigningStatus(null);
      localStorage.removeItem('processingEnvelopes');
    }, 30000);

    return () => clearTimeout(timeout);
  }, [signingInProgress]);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const processingData = localStorage.getItem('processingEnvelopes');
        if (processingData) {
          const { envelopeId } = JSON.parse(processingData);
          if (!processingEnvelopeId) {
            setProcessingEnvelopeId(envelopeId);
            setSigningInProgress(true);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [processingEnvelopeId]);

  // ============================================================================
  // RETURN VALUES
  // ============================================================================

  return {
    // State
    allEnvelopes,
    filteredEnvelopes,
    totalItems,
    searchEmails,
    searchType,
    statusFilters,
    dateFilter,
    customDateRange,
    currentPage,
    itemsPerPage,
    error,
    loading,
    isFetching,
    isPolling,
    hasSearched,
    expandedEnvelopes,
    expandedDetails,
    isPendingView,
    awaitingSignatureCount,
    isAuthLoading,
    userPhoto,
    userDisplayName,
    userEmail,
    isUserInfoLoading,
    signingInProgress,
    signedEnvelopeId,
    processingEnvelopeId,
    signingStatus,
    signingError,
    processingRecipientsMap,
    canReassign,
    reassignModalOpen,
    reassignEnvelopeId,
    reassignRecipient,
    isReassigning,
    reassignmentSuccess,
    sendingReminderKey,
    reminderSuccess,
    voidModalOpen,
    voidEnvelope,
    isVoiding,
    voidSuccess,
    removeDocModalOpen,
    removeDocEnvelopeId,
    removeDocTarget,
    isRemovingDoc,
    removeDocSuccess,
    envelopeDocuments,
    isLoadingDocuments,
    isLoadingDocView,
    viewingDocId,
    remindersUpdatingId,
    remindersSuccess,
    updatesAvailable,
    lastPolled,
    pollingState,
    lastUpdateButtonClick,
    motd,
    motdLoading,
    isMotdAdmin,
    motdModalOpen,
    isSavingMotd,
    activeSearchParams,

    // Setters
    setSearchEmails,
    setSearchType,
    setStatusFilters,
    setDateFilter,
    setCustomDateRange,
    setActiveSearchParams,
    setCurrentPage,
    setItemsPerPage,
    setError,
    setExpandedEnvelopes,
    setExpandedDetails,
    setFilteredEnvelopes,
    setTotalItems,
    setUpdatesAvailable,
    setPollingState,
    setLastUpdateButtonClick,
    setReassignModalOpen,
    setReassignRecipient,
    setReassignEnvelopeId,
    setReassignmentSuccess,
    setReminderSuccess,
    setVoidModalOpen,
    setVoidEnvelope,
    setVoidSuccess,
    setRemoveDocModalOpen,
    setRemoveDocTarget,
    setRemoveDocEnvelopeId,
    setRemoveDocSuccess,
    setMotdModalOpen,
    setSigningError,
    setHasSearched,

    // Functions
    getAccessToken,
    fetchEnvelopes,
    fetchAwaitingSignatureCount,
    handleSearch,
    handleSearchInput,
    resetSearch,
    showPendingEnvelopes,
    toggleEnvelopeView,
    handleSigningRequest,
    getSigningUrl,
    checkSigningCompletion,
    openReassignModal,
    handleReassign,
    handleSendReminder,
    openVoidModal,
    handleVoidEnvelope,
    fetchEnvelopeDocuments,
    handleViewDocument,
    handleSetReminders,
    openRemoveDocModal,
    handleRemoveDocument,
    searchTenantUsers,
    fetchMOTD,
    handleSaveMotd,
    toggleEnvelopeExpansion,
    formatDate,
    hasSequentialSigning,
    getSignerStatus,
    getSignerIcon,
    handleLogout,
    debouncedFetch,
    debug,
  };
}
